import { useState, useMemo, useCallback, useRef } from "react";
import type { TableServerMode } from "./TableServerMode";

interface UseTableSelectionOptions<T> {
  selectable: boolean;
  paginated: boolean;
  displayData: T[];
  sortedData: T[];
  getRowKey: (row: T, index: number) => string;
  onSelectionChange?: ((selectedKeys: Set<string>) => void) | undefined;
  /** Seeds the initial selected set on mount. Subsequent changes are ignored. */
  initialSelectedKeys?: Set<string> | undefined;
  /** True when a row can't be selected — select-all and the counts skip it. */
  isRowDisabled?: ((row: T) => boolean) | undefined;
  /**
   * A server-paged table. When it takes `onAllMatchingSelectionChange`,
   * "select all" means every matching row on the server.
   */
  server?: TableServerMode | undefined;
}

// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function useTableSelection<T>({
  selectable,
  paginated,
  displayData,
  sortedData,
  getRowKey,
  onSelectionChange,
  initialSelectedKeys,
  isRowDisabled,
  server: serverMode,
}: UseTableSelectionOptions<T>) {
  // A server table that can't hold an all-matching selection selects the
  // rows on its page, like any other table.
  const server = serverMode?.onAllMatchingSelectionChange
    ? serverMode
    : undefined;
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(
    () => new Set(initialSelectedKeys ?? []),
  );
  // Mirror for handleSelectRow, so its identity stays stable (memo'd rows
  // only re-render when their own selection changes) while it still reads
  // the current selection.
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;
  const serverRef = useRef(server);
  serverRef.current = server;

  // With every match selected, a row is ticked unless the reader unticked it.
  const allMatching = server?.allMatchingSelection;
  const isRowSelected = (key: string) =>
    allMatching ? !allMatching.excludedKeys.has(key) : selectedKeys.has(key);
  const selectedCount =
    allMatching && server
      ? server.totalRows - allMatching.excludedKeys.size
      : selectedKeys.size;

  // Only the eligible rows. Select-all that ticks rows the user cannot tick
  // individually would report a selection they never made.
  const allPageKeys = useMemo(
    () =>
      displayData
        .filter((row) => !(isRowDisabled?.(row) ?? false))
        .map((row, i) => getRowKey(row, i)),
    [displayData, getRowKey, isRowDisabled],
  );

  const allSelected =
    selectable && allPageKeys.length > 0 && allPageKeys.every(isRowSelected);

  const someSelected =
    selectable && !allSelected && allPageKeys.some(isRowSelected);

  // Keys for ALL rows across all pages (requires rowKey prop for correctness with pagination)
  const allDataKeys = useMemo(
    () =>
      sortedData
        .filter((row) => !(isRowDisabled?.(row) ?? false))
        .map((row, i) => getRowKey(row, i)),
    [sortedData, getRowKey, isRowDisabled],
  );

  const allDataSelected = server
    ? selectable && allMatching?.excludedKeys.size === 0
    : selectable &&
      allDataKeys.length > 0 &&
      allDataKeys.every((k) => selectedKeys.has(k));

  const hasMoreData = server
    ? server.totalRows > displayData.length
    : paginated && sortedData.length > displayData.length;

  const showSelectionBanner = selectable && selectedCount > 0;

  // Untick or re-tick rows while every match is selected. Unticking the last
  // one leaves nothing selected.
  function updateExcluded(
    selection: { excludedKeys: Set<string> },
    change: (excluded: Set<string>) => void,
  ) {
    const excludedKeys = new Set(selection.excludedKeys);
    change(excludedKeys);
    const current = serverRef.current;
    if (current && excludedKeys.size >= current.totalRows) {
      handleClearAllSelection();
      return;
    }
    current?.onAllMatchingSelectionChange?.({ excludedKeys });
  }

  // The parent is notified outside the state updater on purpose: React may run
  // an updater during render, and a parent setState from inside it is the
  // "Cannot update a component while rendering a different component" warning.
  function handleSelectAll() {
    if (allMatching) {
      updateExcluded(allMatching, (excluded) => {
        for (const k of allPageKeys) {
          if (allSelected) excluded.add(k);
          else excluded.delete(k);
        }
      });
      return;
    }
    const next = new Set(selectedKeys);
    if (allSelected) {
      for (const k of allPageKeys) next.delete(k);
    } else {
      for (const k of allPageKeys) next.add(k);
    }
    setSelectedKeys(next);
    onSelectionChange?.(next);
  }

  function handleSelectAllData() {
    // Every match lives in the caller's all-matching selection, and the ticked
    // page keys are dropped: kept, they would outlive that selection when the
    // caller clears it for new filters, leaving rows the reader can't see
    // selected.
    const next = server ? new Set<string>() : new Set(allDataKeys);
    server?.onAllMatchingSelectionChange?.({ excludedKeys: new Set() });
    setSelectedKeys(next);
    onSelectionChange?.(next);
  }

  function handleClearAllSelection() {
    serverRef.current?.onAllMatchingSelectionChange?.(undefined);
    const next = new Set<string>();
    setSelectedKeys(next);
    onSelectionChange?.(next);
  }

  // Read through a ref so handleSelectRow keeps a stable identity.
  const updateExcludedRef = useRef(updateExcluded);
  updateExcludedRef.current = updateExcluded;

  const handleSelectRow = useCallback(
    (key: string) => {
      const selection = serverRef.current?.allMatchingSelection;
      if (selection) {
        updateExcludedRef.current(selection, (excluded) => {
          if (excluded.has(key)) excluded.delete(key);
          else excluded.add(key);
        });
        return;
      }
      const next = new Set(selectedKeysRef.current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setSelectedKeys(next);
      onSelectionChange?.(next);
    },
    [onSelectionChange],
  );

  return {
    selectedKeys,
    selectedCount,
    isRowSelected,
    allSelected,
    someSelected,
    allDataSelected,
    hasMoreData,
    showSelectionBanner,
    handleSelectAll,
    handleSelectAllData,
    handleClearAllSelection,
    handleSelectRow,
  };
}
